import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useIsSmallerThanTablet } from '../../responsive/hooks/hooks';
import { DivButton } from './DivButton';
import { ChevronLeftIcon, DividerIcon } from '../../assets/icons';
import clsx from 'clsx';
import { v4 as uuidv4 } from 'uuid';
import { ProgressBar } from 'react-bootstrap';

export type ItemListAndDetailsLayout = 'two-column' | 'slim';
export type ItemListAndDetailsView = 'list' | 'details';

export type ItemListProps<TItem> = {
  items: TItem[] | null;
  itemsLoadingComponent: () => ReactElement;
  selectedItem: TItem | null;
  onItemSelected: (item: TItem | null) => void;
  idGetter: (item: TItem) => string;
  nameGetter: (item: TItem) => string;
  categoryGetter?: (item: TItem) => string | null;
};

export type ItemDetailsProps<TItem, TActionPayload> = {
  item: TItem;
  headerPortal?: HTMLDivElement | null;
  onAction?: (payload: TActionPayload) => void;
  onNavigateTo: (indexOrId: number | string) => void;
};

export const ItemListAndDetails = <TItem, TActionPayload, TItemListExtraProps>(props: {
  items: TItem[] | null;
  itemsLoadingComponent: ReactElement;
  itemListExtraProps: TItemListExtraProps;
  itemListComponent: (props: ItemListProps<TItem> & TItemListExtraProps) => ReactElement;
  detailsComponent: (props: ItemDetailsProps<TItem, TActionPayload>) => ReactElement;
  defaultSelectedItemId?: string | null;
  onItemSelected?: (item: TItem | null) => void;
  layout?: ItemListAndDetailsLayout;
  defaultView?: ItemListAndDetailsView;
  nameGetter: (item: TItem) => string;
  idGetter: (item: TItem) => string;
  headerClassNameGetter?: (item: TItem) => string;
  categoryGetter?: (item: TItem) => string | null;
  progress?: number;
  onAction?: (payload: TActionPayload) => void;
}) => {
  const {
    items,
    itemsLoadingComponent,
    itemListExtraProps,
    itemListComponent: ItemList,
    detailsComponent: Details,
    defaultSelectedItemId,
    onItemSelected,
    layout,
    defaultView,
    nameGetter,
    idGetter,
    categoryGetter,
    headerClassNameGetter,
    progress,
    onAction,
  } = props;
  const [id] = useState(uuidv4());

  const [selectedItem, setSelectedItem] = useState<TItem | null>(
    items?.find((x) => defaultSelectedItemId && idGetter(x) === defaultSelectedItemId) ??
      (items ? (items.length > 0 ? items[0] : null) : null)
  );
  const [headerPortal, setHeaderPortal] = useState<HTMLDivElement | null>(null);
  const [view, setView] = useState<'list' | 'details'>(defaultView ?? 'list');
  const isSmallerThanTablet = useIsSmallerThanTablet();
  const effectiveLayout: ItemListAndDetailsLayout =
    layout === 'slim' ? 'slim' : isSmallerThanTablet ? 'slim' : 'two-column';

  const showList = effectiveLayout === 'two-column' || view === 'list' || selectedItem === null;
  const showDetails =
    effectiveLayout === 'two-column' || (view === 'details' && selectedItem !== null);

  useEffect(() => {
    if (!items) {
      if (selectedItem) setSelectedItem(null);
      return;
    }
    const firstItemOrNull = items.length > 0 ? items[0] : null;
    if (!selectedItem) {
      setSelectedItem(firstItemOrNull);
      return;
    }
    const freshSelectedItem =
      items.find((x) => idGetter(x) === idGetter(selectedItem)) ?? firstItemOrNull;
    setSelectedItem(freshSelectedItem);
  }, [selectedItem, items, idGetter]);

  const onNavigateTo = useCallback(
    (indexOrId: number | string) => {
      const nextItem =
        typeof indexOrId === 'string'
          ? items?.find((v) => idGetter(v) === indexOrId)
          : !!items && items.length > indexOrId
          ? items[indexOrId]
          : null;
      if (nextItem) {
        setSelectedItem(nextItem);
        setView('details');
      }
    },
    [items, idGetter]
  );
  const adjustedProgress = progress ? Math.min(Math.max(Math.floor(progress), 0), 100) : undefined;
  return (
    <div
      className={
        effectiveLayout === 'two-column' ? 'item-list-and-details__container' : 'messenger__slim'
      }
    >
      {showList && (
        <ItemList
          items={items}
          itemsLoadingComponent={() => itemsLoadingComponent}
          selectedItem={selectedItem}
          onItemSelected={(item) => {
            setSelectedItem(item);
            onItemSelected && onItemSelected(item);
            setView('details');
          }}
          idGetter={idGetter}
          nameGetter={nameGetter}
          categoryGetter={categoryGetter}
          {...itemListExtraProps}
        />
      )}
      <div className={showDetails ? 'item-list-and-details__detail-placeholder' : 'd-none'}>
        <div className="item-list-and-details__header">
          <>
            {effectiveLayout === 'slim' && view === 'details' && (
              <DivButton
                className="item-list-and-details__back-button"
                dataTest="item-list-and-details__back-button"
                icon={<ChevronLeftIcon />}
                onClick={() => setView('list')}
              />
            )}
            <div
              className={`item-list-and-details__detail-title-container ${
                !!headerClassNameGetter && selectedItem ? headerClassNameGetter(selectedItem) : ''
              }`}
            >
              {categoryGetter && selectedItem && categoryGetter(selectedItem) && (
                <>
                  <div className="item-list-and-details__detail-title">
                    {categoryGetter(selectedItem)}
                  </div>
                  <DividerIcon className="flex-shrink-0" />
                </>
              )}
              {selectedItem && (
                <div className="item-list-and-details__detail-title">
                  {nameGetter(selectedItem)}
                </div>
              )}
              {adjustedProgress !== undefined && (
                <div className="item-list-and-details__progress-bar">
                  <ProgressBar
                    striped
                    variant="info"
                    now={adjustedProgress}
                    label={
                      adjustedProgress > 10 ? (
                        <span className="font-weight-bold">{`${adjustedProgress}%`}</span>
                      ) : undefined
                    }
                  />
                </div>
              )}
            </div>
          </>
          <div
            ref={(x) => setHeaderPortal(x)}
            className={clsx('gpt-model-selector__wrapper', {
              'd-none': !(selectedItem !== null),
            })}
            id={`item-list-and-details-top-placeholder-${id}`}
          />
        </div>

        {selectedItem && (effectiveLayout === 'two-column' || view === 'details') && (
          <Details
            key={idGetter(selectedItem)}
            item={selectedItem}
            headerPortal={headerPortal}
            onAction={onAction}
            onNavigateTo={onNavigateTo}
          />
        )}
      </div>
    </div>
  );
};
